import { useState } from 'react';
import type { FormationPlayer, MatchLineups, MatchSideLineup } from '@mondy/shared';

/**
 * Vertical, head-to-head pitch. The home side fills the top half (its keeper at
 * the very top, advancing toward the halfway line); the away side fills the
 * bottom half rotated 180° (keeper at the very bottom, mirrored across both
 * axes) so the two formations face each other like a real teamsheet. Each
 * starter is a circular avatar (player photo from API-Football's media CDN,
 * ringed in the team colour, with a shirt-number badge + surname; falls back to
 * a jersey icon when no photo is available), positioned from API-Football's
 * "row:col" grid (row 1 = keeper). Falls back to position-based rows for any
 * player missing a grid.
 */
export function FormationPitch({
  lineups,
  homeLabel,
  awayLabel,
  homeColor = '#e8483f',
  awayColor = '#3f7be8',
}: {
  lineups: MatchLineups;
  homeLabel: string;
  awayLabel: string;
  homeColor?: string;
  awayColor?: string;
}) {
  const home = lineups.team1;
  const away = lineups.team2;
  const homeStarters = home?.starters ?? [];
  const awayStarters = away?.starters ?? [];

  if (homeStarters.length === 0 && awayStarters.length === 0) {
    return (
      <p style={{ textAlign: 'center', color: 'var(--fg-muted)', padding: '48px 20px' }}>
        Line-ups are published around 20–40 minutes before kick-off.
      </p>
    );
  }

  return (
    <div>
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 460,
          margin: '0 auto',
          aspectRatio: '3 / 4',
          borderRadius: 16,
          overflow: 'hidden',
          background: 'linear-gradient(180deg, #1f7a43 0%, #156234 50%, #1f7a43 100%)',
          border: '1px solid rgba(255,255,255,0.14)',
        }}
      >
        <PitchMarkings />

        <SideBadge formation={home?.formation ?? null} corner="top" />
        <SideBadge formation={away?.formation ?? null} corner="bottom" />

        {placeSide(homeStarters, 'home').map((pp, i) => (
          <PlayerMarker key={`h-${i}`} player={pp.player} xPct={pp.xPct} yPct={pp.yPct} color={homeColor} />
        ))}
        {placeSide(awayStarters, 'away').map((pp, i) => (
          <PlayerMarker key={`a-${i}`} player={pp.player} xPct={pp.xPct} yPct={pp.yPct} color={awayColor} />
        ))}
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 16 }}>
        <Bench label={homeLabel} side={home} accent={homeColor} />
        <Bench label={awayLabel} side={away} accent={awayColor} />
      </div>
    </div>
  );
}

interface Placed {
  player: FormationPlayer;
  xPct: number;
  yPct: number;
}

// Map a starting XI onto the relevant half. `home` fills the top (keeper at the
// top edge, rows advancing down to the halfway line); `away` is point-mirrored
// into the bottom half (keeper at the bottom edge) — a true 180° rotation of the
// formation so the teams meet head-to-head at the centre. Per-player left/right
// handedness is resolved against API-Football's grid columns below.
function placeSide(starters: FormationPlayer[], side: 'home' | 'away'): Placed[] {
  if (starters.length === 0) return [];

  const rowOf = (p: FormationPlayer): number => {
    const r = p.grid ? Number(p.grid.split(':')[0]) : NaN;
    if (Number.isFinite(r)) return r;
    return { GK: 1, DEF: 2, MID: 3, FWD: 4 }[p.pos ?? 'MID'] ?? 3;
  };
  const colOf = (p: FormationPlayer): number => {
    const c = p.grid ? Number(p.grid.split(':')[1]) : NaN;
    return Number.isFinite(c) ? c : 0;
  };

  const byRow = new Map<number, FormationPlayer[]>();
  for (const p of starters) {
    const r = rowOf(p);
    const bucket = byRow.get(r) ?? [];
    bucket.push(p);
    byRow.set(r, bucket);
  }
  const rowKeys = [...byRow.keys()].sort((a, b) => a - b);
  const rowCount = rowKeys.length;

  const placed: Placed[] = [];
  rowKeys.forEach((rk, ri) => {
    const line = byRow.get(rk)!.slice().sort((a, b) => colOf(a) - colOf(b));
    const depth = rowCount <= 1 ? 0 : ri / (rowCount - 1); // 0 = keeper, 1 = furthest forward
    // Keeper near the goal line (5% / 95%), advancing to just shy of halfway.
    const yPct = side === 'home' ? 5 + depth * 40 : 95 - depth * 40;
    line.forEach((player, idx) => {
      const spread = ((idx + 1) / (line.length + 1)) * 100;
      // API-Football column 1 = the player's own left (left back / left wing).
      // Home defends the top goal and attacks DOWN-screen, so its left flank sits
      // on the viewer's right → reverse. Away attacks UP-screen (standard
      // orientation), so its left flank is on the viewer's left → keep. This also
      // makes away a true 180° rotation of home.
      const xPct = side === 'home' ? 100 - spread : spread;
      placed.push({ player, xPct, yPct });
    });
  });
  return placed;
}

function PlayerMarker({
  player,
  xPct,
  yPct,
  color,
}: {
  player: FormationPlayer;
  xPct: number;
  yPct: number;
  color: string;
}) {
  return (
    <div
      style={{
        position: 'absolute',
        left: `${xPct}%`,
        top: `${yPct}%`,
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        width: 60,
      }}
    >
      <Avatar photoUrl={player.photoUrl} number={player.number} color={color} />
      <span
        style={{
          fontSize: 10,
          lineHeight: 1.15,
          color: '#fff',
          textAlign: 'center',
          textShadow: '0 1px 2px rgba(0,0,0,0.8)',
          maxWidth: 60,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {shortName(player.name)}
      </span>
    </div>
  );
}

/**
 * Circular player photo from API-Football's media CDN, ringed in the team
 * colour with the shirt number as a corner badge. Falls back to the jersey
 * icon when there's no photo URL or the image 404s (the CDN lacks a headshot).
 */
function Avatar({ photoUrl, number, color }: { photoUrl: string | null; number: number | null; color: string }) {
  const [errored, setErrored] = useState(false);

  if (!photoUrl || errored) {
    return <Jersey color={color} number={number} />;
  }

  return (
    <div style={{ position: 'relative', width: 40, height: 40 }}>
      <img
        src={photoUrl}
        alt=""
        loading="lazy"
        onError={() => setErrored(true)}
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          objectFit: 'cover',
          background: '#e9eef2',
          border: `2px solid ${color}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.45)',
          display: 'block',
        }}
      />
      {number != null ? (
        <span
          style={{
            position: 'absolute',
            bottom: -2,
            right: -2,
            minWidth: 16,
            height: 16,
            padding: '0 3px',
            borderRadius: 8,
            background: color,
            border: '1.5px solid #fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            fontWeight: 800,
            color: '#fff',
            lineHeight: 1,
          }}
        >
          {number}
        </span>
      ) : null}
    </div>
  );
}

/** Team-coloured shirt icon with the shirt number on the torso. */
function Jersey({ color, number }: { color: string; number: number | null }) {
  return (
    <div style={{ position: 'relative', width: 32, height: 30 }}>
      <svg viewBox="0 0 48 44" width={32} height={30} aria-hidden style={{ display: 'block' }}>
        <path
          d="M17 4 L5 9 L10 20 L16 17 L16 41 L32 41 L32 17 L38 20 L43 9 L31 4 C28 8 20 8 17 4 Z"
          fill={color}
          stroke="rgba(255,255,255,0.9)"
          strokeWidth={1.6}
          strokeLinejoin="round"
        />
      </svg>
      {number != null ? (
        <span
          style={{
            position: 'absolute',
            inset: 0,
            top: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 800,
            color: '#fff',
            textShadow: '0 1px 1.5px rgba(0,0,0,0.85)',
          }}
        >
          {number}
        </span>
      ) : null}
    </div>
  );
}

function SideBadge({ formation, corner }: { formation: string | null; corner: 'top' | 'bottom' }) {
  if (!formation) return null;
  return (
    <span
      className="mono"
      style={{
        position: 'absolute',
        [corner]: 8,
        left: 8,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.12em',
        color: '#fff',
        background: 'rgba(0,0,0,0.32)',
        borderRadius: 6,
        padding: '2px 6px',
      }}
    >
      {formation}
    </span>
  );
}

function Bench({ label, side, accent }: { label: string; side: MatchSideLineup | null; accent: string }) {
  const subs = side?.subs ?? [];
  return (
    <div style={{ flex: '1 1 220px', minWidth: 200 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: accent }} aria-hidden />
        <span style={{ fontWeight: 700 }}>{label}</span>
        {side?.formation ? (
          <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)', letterSpacing: '0.12em' }}>
            {side.formation}
          </span>
        ) : null}
      </div>
      {subs.length > 0 ? (
        <div style={{ fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.7 }}>
          <span style={{ fontWeight: 600, color: 'var(--fg)' }}>Bench: </span>
          {subs.map((s) => `${s.number ? `${s.number} ` : ''}${shortName(s.name)}`).join(', ')}
        </div>
      ) : null}
    </div>
  );
}

function PitchMarkings() {
  const line = 'rgba(255,255,255,0.32)';
  return (
    <div aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {/* halfway line */}
      <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 2, background: line }} />
      {/* centre circle */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '26%',
          aspectRatio: '1',
          transform: 'translate(-50%, -50%)',
          border: `2px solid ${line}`,
          borderRadius: '50%',
        }}
      />
      {/* penalty boxes (top + bottom) */}
      <div style={{ position: 'absolute', top: 0, left: '27%', right: '27%', height: '13%', borderLeft: `2px solid ${line}`, borderRight: `2px solid ${line}`, borderBottom: `2px solid ${line}` }} />
      <div style={{ position: 'absolute', bottom: 0, left: '27%', right: '27%', height: '13%', borderLeft: `2px solid ${line}`, borderRight: `2px solid ${line}`, borderTop: `2px solid ${line}` }} />
    </div>
  );
}

// "Bruno Guimarães" → "Guimarães"; single token stays as-is.
function shortName(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts.length > 1 ? parts[parts.length - 1]! : name;
}
